import { type AxiosInstance } from "axios";
import useAxiosAuth from "../hooks/useAxiosAuth.ts";
import { useQuery, useMutation } from "@tanstack/react-query";

const endpoints = {
  one_mile_analysis: "sites/one_mile_report/",
  normal_analysis: "sites/full_report/",
  "20_mile_analysis": "sites/custom_miles_report/",
};

const transformFullReport = (response: any) => {
  return response.data;
};

const defaultOneTenMile = {
  one_mile_children: [],
  one_mile_parents: [],
  one_mile_residential: [],
  ten_mile_children: [],
  ten_mile_parents: [],
  ten_mile_residential: [],
};

const defaultTwentyMile = {
  child_sites: [],
  parent_sites: [],
};

const fetchFullReport = async (
  axiosAuth: AxiosInstance,
  filters: any,
  analysis_type: string
) => {
  const endpoint = endpoints[analysis_type] ?? endpoints.normal_analysis;
  const url = new URL(
    `${process.env.REACT_APP_API_BASE_URL}${endpoint}`
  );
  Object.entries(filters).forEach(([filterKey, value]: any) => {
    const stringValue = value.toString();
    if (value.length > 0) {
      url.searchParams.append(filterKey, stringValue);
    }
  });

  if (analysis_type === "20_mile_analysis") {
    url.searchParams.append("miles", "20");
  }

  try {
    const res = await axiosAuth.get(url.href);

    if (analysis_type === "one_mile_analysis" && res?.data) {
      return {
        ...res,
        data: {
          one_mile_parents: res.data.one_parents ?? [],
          one_mile_children: res.data.one_children ?? [],
          ten_mile_parents: [],
          ten_mile_children: [],
        },
      };
    }

    return res;
  } catch (e) {
    console.error("Full report fetch failed", e);
    const fallback =
      analysis_type === "20_mile_analysis" ? defaultTwentyMile : defaultOneTenMile;
    return { data: fallback };
  }
};

export function useFullReport(
  session: any,
  filters: any,
  analysis_type: string,
  shouldFetch: boolean
) {
  const axiosAuth = useAxiosAuth();
  return useQuery({
    queryKey: ["fullReport", filters, analysis_type],
    queryFn: async () =>
      await fetchFullReport(axiosAuth, filters, analysis_type),
    select: (data) => transformFullReport(data),
    enabled: !!session?.user && shouldFetch,
    refetchOnWindowFocus: false,
  });
}

export function usePostReport() {
  const axiosAuth = useAxiosAuth();
  return useMutation({
    mutationFn: async (parameters: any) => {
      const endpoint =
        endpoints[parameters.analysis] ?? endpoints.normal_analysis;
      const url = new URL(
        `${process.env.REACT_APP_API_BASE_URL}${endpoint}`
      );
      let payload: any = {};
      Object.entries(parameters.filters).forEach(([filterKey, value]: any) => {
        const stringValue = value.toString();
        if (value.length > 0) {
          payload[filterKey] = stringValue;
        }
      });
      if (parameters.analysis === "20_mile_analysis") {
        payload["miles"] = 20;
      }
      return await axiosAuth.post(url.href, payload, {
        responseType: "blob",
      });
    },
  });
}
